/**
 * SuggestedFollowups — Clickable follow-up chip list
 * MAESTRO:P0D
 *
 * Renders up to 3 suggested follow-up questions as orange-outlined chips.
 * Clicking a chip fires it as a new message via the provided onSelect callback.
 * Keyboard accessible: Enter/Space activates the focused chip.
 */

import { useCallback } from 'react';

/**
 * @param {Object} props
 * @param {string[]} props.suggestions - Array of follow-up question strings (max 3 shown)
 * @param {(text: string) => void} props.onSelect - Callback when a chip is activated
 * @param {boolean} [props.disabled] - Disable chips while loading
 */
export default function SuggestedFollowups({ suggestions = [], onSelect, disabled = false }) {
  const visible = suggestions.slice(0, 3);

  const handleKeyDown = useCallback((e, text) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!disabled) onSelect(text);
    }
  }, [disabled, onSelect]);

  if (!visible.length) return null;

  return (
    <div
      className="mt-3 flex flex-wrap gap-2"
      role="group"
      aria-label="Suggested follow-up questions"
    >
      {visible.map((text, i) => (
        <button
          key={i}
          type="button"
          onClick={() => !disabled && onSelect(text)}
          onKeyDown={(e) => handleKeyDown(e, text)}
          disabled={disabled}
          aria-label={`Ask: ${text}`}
          className={`
            text-xs font-mono px-3 py-1.5 rounded-full border transition-all duration-150 text-left
            min-h-[2rem] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 focus:ring-offset-zinc-950
            ${disabled
              ? 'border-zinc-700 text-zinc-600 cursor-not-allowed'
              : 'border-orange-500/60 text-orange-400 hover:bg-orange-500/10 hover:border-orange-500 cursor-pointer active:scale-95'
            }
          `}
        >
          {text}
        </button>
      ))}
    </div>
  );
}
